import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { studentApi } from '@/api';
import { resolveFileUrl } from '@/api/client';
import { Button, ConfirmDialog, Icon, useToast } from '@/components/ui';
import { errorMessage } from '@/hooks/useApiError';

const MAX_BYTES = 2 * 1024 * 1024;
const ACCEPT = 'image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp';
const TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

/**
 * The profile picture, at the top of "About you".
 *
 * Its own uploads, not a form field: an image is saved the moment it is
 * chosen, so the buttons are `type="button"` and never submit the form around
 * them. A picture that only took effect if you remembered to press Save would
 * be the one field on the page that behaved differently from the rest.
 *
 * It does not count towards profile completion, and the copy says so: a
 * percentage point for a photograph would be the platform telling students
 * that showing their face improves their chances.
 */
export function ProfilePhotoField({ imageUrl, fullName }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const input = useRef(null);
  const [confirming, setConfirming] = useState(false);

  /** Both endpoints answer with the whole profile; the cache takes their word. */
  function keep(profile) {
    queryClient.setQueryData(['student-profile'], profile);
  }

  const upload = useMutation({
    mutationFn: (file) => studentApi.uploadPhoto(file),
    onSuccess: (profile) => {
      toast.success('Profile picture updated');
      keep(profile);
    },
    onError: (error) => toast.error(errorMessage(error, 'Could not upload that picture')),
  });

  const remove = useMutation({
    mutationFn: () => studentApi.removePhoto(),
    onSuccess: (profile) => {
      toast.success('Profile picture removed');
      keep(profile);
      setConfirming(false);
    },
    onError: (error) => toast.error(errorMessage(error, 'Could not remove the picture')),
  });

  function choose(event) {
    const file = event.target.files?.[0];
    // Cleared so choosing the same file again still fires a change.
    event.target.value = '';
    if (!file) return;
    if (!TYPES.has(file.type)) {
      toast.error('Choose a JPEG, PNG or WebP image.');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error('That picture is over 2 MB. Choose a smaller one.');
      return;
    }
    upload.mutate(file);
  }

  const initials = (fullName ?? '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');

  return (
    <div className="flex flex-wrap items-center gap-4">
      {imageUrl ? (
        <img
          src={resolveFileUrl(imageUrl)}
          alt={fullName ? `Profile picture of ${fullName}` : 'Your profile picture'}
          className="border-line size-16 shrink-0 rounded-full border object-cover"
        />
      ) : (
        <div
          className="bg-brand-50 text-brand-700 flex size-16 shrink-0 items-center justify-center rounded-full text-xl font-semibold"
          aria-hidden="true"
        >
          {initials || '?'}
        </div>
      )}

      <div className="min-w-0 flex-1">
        <p className="text-ink text-[12px] font-medium">Profile picture</p>
        <p className="text-ink-muted mt-0.5 text-xs">
          Optional · JPEG, PNG or WebP, up to 2 MB. It does not count towards your completion.
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            isLoading={upload.isPending}
            onClick={() => input.current?.click()}
          >
            <Icon name="arrowUp" size={15} />
            {imageUrl ? 'Change picture' : 'Upload picture'}
          </Button>
          {imageUrl && (
            <Button
              type="button"
              variant="ghostDanger"
              size="sm"
              onClick={() => setConfirming(true)}
            >
              Remove
            </Button>
          )}
        </div>
        <input
          ref={input}
          type="file"
          accept={ACCEPT}
          className="sr-only"
          tabIndex={-1}
          aria-hidden="true"
          onChange={choose}
        />
      </div>

      {/* Out of the profile form in the DOM, so its buttons cannot submit it. */}
      {createPortal(
        <ConfirmDialog
          open={confirming}
          title="Remove your picture?"
          message="Your profile falls back to your initials. You can upload another at any time."
          confirmLabel="Remove"
          isLoading={remove.isPending}
          onConfirm={() => remove.mutate()}
          onCancel={() => setConfirming(false)}
        />,
        document.body,
      )}
    </div>
  );
}
